import React, { Component, useEffect } from 'react';
import LessonLayout from '../components/LessonLayout';
import { Card, Row, Col, Alert } from 'react-bootstrap';

// METHOD 1: Functional Component (using function keyword)
function FunctionalComponent() {
  console.log('✅ Functional Component Rendered (function keyword)');
  return (
    <div className="demo-box mb-3">
      <h5>Functional Component</h5>
      <p>I am a Functional Component created with the <code>function</code> keyword</p>
    </div>
  );
}

// METHOD 2: Arrow Function Component
const ArrowComponent = () => {
  console.log('✅ Arrow Component Rendered (arrow function)');
  return (
    <div className="demo-box mb-3">
      <h5>Arrow Function Component</h5>
      <p>I am an Arrow Function Component created with <code>=&gt;</code></p>
    </div>
  );
};

// METHOD 3: Class Component
class ClassComponent extends Component {
  componentDidMount() {
    console.log('✅ Class Component Mounted (lifecycle method)');
  }

  render() {
    console.log('✅ Class Component Rendered (render method)');
    return (
      <div className="demo-box mb-3">
        <h5>Class Component</h5>
        <p>I am a Class Component extending <code>React.Component</code></p>
      </div>
    );
  }
}

// JSX Expressions Demo
const JSXExpressions = () => {
  const name = 'React';
  const age = 10;
  const features = ['Components', 'JSX', 'Virtual DOM'];

  const getGreeting = (name) => `Hello, ${name}!`;

  console.log('🔢 JSX Expressions Demo - Variables:', { name, age, features });
  
  return (
    <Card className="mb-3">
      <Card.Header>JSX Expressions</Card.Header>
      <Card.Body>
        <p><strong>Variable in JSX:</strong> {name}</p>
        <p><strong>Expression:</strong> {age} years old</p>
        <p><strong>Math Expression:</strong> Double age = {age * 2}</p>
        <p><strong>Ternary Operator:</strong> Status: {age > 5 ? 'Mature 🎉' : 'Young 🌱'}</p>
        <p><strong>Function Call:</strong> {getGreeting(name)}</p>
        <p><strong>Array Length:</strong> Features count = {features.length}</p>
        <p><strong>Template Literal:</strong> {`${name} is ${age} years old`}</p>
      </Card.Body>
    </Card>
  );
};

// Component Composition Demo
const ParentComponent = () => {
  console.log('📦 Parent Component Rendered - Demonstrating composition');
  
  return (
    <Card className="mb-3">
      <Card.Header>Component Composition</Card.Header>
      <Card.Body>
        <p>This is a parent component that contains child components:</p>
        <ChildComponent1 />
        <ChildComponent2 />
      </Card.Body>
    </Card>
  );
};

const ChildComponent1 = () => {
  console.log('   📦 Child Component 1 Rendered');
  return <div className="alert alert-success">I am Child Component 1</div>;
};

const ChildComponent2 = () => {
  console.log('   📦 Child Component 2 Rendered');
  return <div className="alert alert-info">I am Child Component 2</div>;
};

// Main Lesson Component
const Lesson01_Components = () => {
  useEffect(() => {
    console.log('📖 Lesson 01: Components & JSX - Loaded');
    console.log('📚 Learning Objectives:');
    console.log('   1. Understand different component types');
    console.log('   2. Master JSX syntax and expressions');
    console.log('   3. Learn component composition');
  }, []);

  return (
    <LessonLayout 
      lessonNumber={1}
      title="Components & JSX" 
      description="Understanding React Components and JSX Syntax"
    >
      <Alert variant="success">
        <strong>What You'll Learn:</strong>
        <ul className="mb-0 mt-2">
          <li>Three ways to create components (Function, Arrow, Class)</li>
          <li>JSX syntax and expressions</li>
          <li>Component composition and nesting</li>
        </ul>
      </Alert>

      <h4 className="mt-4">1. Component Types</h4>
      <Row>
        <Col md={12}>
          <FunctionalComponent />
          <ArrowComponent />
          <ClassComponent />
        </Col>
      </Row>

      <h4 className="mt-4">2. JSX Expressions</h4>
      <JSXExpressions />

      <h4 className="mt-4">3. Component Composition</h4>
      <ParentComponent />

      <h4 className="mt-4">📊 Comparison Table</h4>
      <Card>
        <Card.Body>
          <table className="table comparison-table">
            <thead>
              <tr>
                <th>Component Type</th>
                <th>Syntax</th>
                <th>Use Case</th>
                <th>Hooks Support</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Functional (function)</td>
                <td><code>function MyComponent() {'{}'}</code></td>
                <td>Preferred modern approach</td>
                <td>✅ Yes</td>
              </tr>
              <tr>
                <td>Arrow Function</td>
                <td><code>const MyComponent = () =&gt; {'{}'}</code></td>
                <td>Concise syntax, very common</td>
                <td>✅ Yes</td>
              </tr>
              <tr>
                <td>Class Component</td>
                <td><code>class MyComponent extends Component {'{}'}</code></td>
                <td>Legacy code, lifecycle methods</td>
                <td>❌ No (uses lifecycle)</td>
              </tr>
            </tbody>
          </table>
        </Card.Body>
      </Card>

      <Alert variant="warning" className="mt-4">
        <strong>💡 Best Practice:</strong> Modern React prefers functional components with hooks over class components.
      </Alert>
    </LessonLayout>
  );
};

export default Lesson01_Components;
